import { Currency, Form, SingleDatePicker } from '@mairistem/composants';
import { useUpdateEffect } from '@mairistem/tools';
import dayjs from 'dayjs';
import _ from 'lodash';
import React from 'react';

import type { BonCCASType } from '@/types';

interface BonCCASProps {
  disabled: boolean;
  reste: number;
  numero: string;
  dateValidite: string;
  montant: number;
  onChange: (value: BonCCASType, complet: boolean) => void;
  onError?: (error: string) => void;
}

const BonCCAS = (props: BonCCASProps) => {
  const {
    numero: numeroProps,
    dateValidite: dateProps,
    montant: montantProps,
    disabled,
    reste,
    onChange,
    onError,
  } = props;

  const [numero, setNumero] = React.useState<string>(numeroProps || '');
  const [date, setDate] = React.useState<dayjs.Dayjs>(dateProps ? dayjs(dateProps) : null);
  const [montant, setMontant] = React.useState<number>(montantProps || 0);

  const handleChange = (value, setter) => {
    setter(value);
  };

  const isComplet = React.useCallback(
    () => !_.isNil(date) && !_.isEmpty(numero) && montant > 0 && montant <= reste,
    [numero, date, montant, reste],
  );

  useUpdateEffect(() => {
    if (!disabled) {
      onChange(
        {
          ...(date !== null && { dateValidite: date?.format('DD/MM/YYYY') }),
          ...(numero !== '' && { numero }),
          ...(montant !== 0 && { montant }),
        },
        isComplet(),
      );
    }
  }, [disabled, numero, date, montant, isComplet]);

  useUpdateEffect(() => {
    if (montant !== 0 && montant > reste && onError) {
      onError('Le montant ne peut pas être supérieur au reste');
    }
  }, [montant, reste]);

  return (
    <Form.Group style={{ marginTop: 0, marginBottom: 0 }}>
      <Form.Input
        // data-testid="numeroBonCcas"
        disabled={disabled}
        autoFocus={!disabled}
        value={numero}
        label="N°"
        inline
        style={{ width: 100 }}
        onChange={(e, { value }) => handleChange(value, setNumero)}
        input={{ 'data-testid': 'numeroBonCcas' }}
        required
      />
      <Form.Field inline required>
        <label>Date de validité</label>
        <SingleDatePicker
          data-testid="dateValiditeBonCcas"
          disabled={disabled}
          clearable
          value={date}
          onChange={(val: dayjs.Dayjs) => setDate(val)}
          disabledDate={(d) => d.isBefore(dayjs().startOf('day'))}
        />
      </Form.Field>
      <Currency
        data-testid="montantBonCcas"
        disabled={disabled}
        value={montant}
        label="Montant"
        inline
        placeholder=""
        style={{ width: 100 }}
        valueMin={0}
        valueMax={reste}
        description=""
        onBlur={(e, value) => handleChange(value, setMontant)}
        required
      />
    </Form.Group>
  );
};

export default BonCCAS;
